import React, { useMemo, useState } from 'react';
import { X, Play } from 'lucide-react';
import { useQuiz } from '../context/QuizContext';
import { typeShortCode } from '../mock';
import { t } from '../i18n';

const PreviewModal = ({ onClose }) => {
  const { quizTitle, questions, lang } = useQuiz();
  const [examMode, setExamMode] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const setAns = (id, val) => setAnswers(a => ({ ...a, [id]: val }));

  const evaluate = () => {
    let correct = 0;
    let total = 0;
    questions.forEach(q => {
      const user = answers[q.id];
      switch (q.type) {
        case 'mc-single': {
          total++;
          if (user === q.correct) correct++;
          break;
        }
        case 'mc-multiple': {
          total++;
          const set1 = new Set(user || []);
          const set2 = new Set(q.corrects || []);
          if (set1.size === set2.size && [...set1].every(v => set2.has(v))) correct++;
          break;
        }
        case 'truefalse': {
          total++;
          if (String(user) === String(q.correct)) correct++;
          break;
        }
        case 'short': {
          total++;
          const acc = (q.answers || []).map(a => a.trim().toLowerCase());
          if (acc.includes((user || '').trim().toLowerCase())) correct++;
          break;
        }
        case 'numerical': {
          total++;
          const u = parseFloat(user);
          const a = parseFloat(q.answer);
          const tol = parseFloat(q.tolerance || 0);
          if (!isNaN(u) && !isNaN(a) && Math.abs(u - a) <= tol) correct++;
          break;
        }
        case 'matching': {
          const pairs = q.pairs || [];
          pairs.forEach((p, i) => {
            total++;
            const u = (user && user[i]) || '';
            if (u.trim() === p.a.trim()) correct++;
          });
          break;
        }
        // essay: not auto-graded
        default: break;
      }
    });
    return { correct, total };
  };

  const score = useMemo(() => submitted ? evaluate() : null, [submitted, answers, questions]);
  const pct = score && score.total > 0 ? Math.round(score.correct / score.total * 100) : 0;

  const startExam = () => { setExamMode(true); setSubmitted(false); setAnswers({}); };
  const submit = () => setSubmitted(true);

  return (
    <div className="mg-modal-overlay" onClick={onClose}>
      <div className="mg-modal" onClick={e => e.stopPropagation()}>
        <div className="mg-modal-head">
          <div className="mg-title" style={{ fontSize: 18 }}>{t('preview', lang)}: {quizTitle || 'Untitled'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!examMode ? (
              <button className="mg-btn warning" style={{ width:'auto', padding:'6px 12px' }} onClick={startExam}>
                <Play size={14} /> {t('startExam', lang)}
              </button>
            ) : !submitted ? (
              <button className="mg-btn success" style={{ width:'auto', padding:'6px 12px' }} onClick={submit}>{t('submitExam', lang)}</button>
            ) : (
              <button className="mg-btn ghost" style={{ width:'auto', padding:'6px 12px' }} onClick={startExam}>Reset</button>
            )}
            <button className="mg-close-x" style={{ position: 'static' }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="mg-modal-body">
          {submitted && (
            <div className="mg-score-card">
              <div style={{ fontSize: 22, fontWeight: 800 }}>{t('yourScore', lang)}: {score.correct} / {score.total} ({pct}%)</div>
              <div style={{ marginTop: 4, color: '#334155' }}>{lang === 'id' ? 'Jawaban yang benar disorot hijau, salah merah.' : 'Correct answers are highlighted green, incorrect red.'}</div>
            </div>
          )}

          {questions.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>{t('noQuestions', lang)}</div>
          )}

          {questions.map((q, idx) => (
            <div className="mg-preview-q" key={q.id}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: 6 }}>
                <span className="mg-badge">#{idx+1} · {typeShortCode(q.type)}</span>
                <strong>{q.title}</strong>
              </div>
              <div style={{ marginBottom: 10 }} dangerouslySetInnerHTML={{ __html: q.question || '<em style="color:#999">(no question)</em>' }} />

              {(q.type === 'mc-single' || q.type === 'mc-multiple') && (q.options || []).map((opt, i) => {
                const correctSet = q.type === 'mc-single' ? [q.correct] : (q.corrects || []);
                const chosen = q.type === 'mc-single' ? (answers[q.id] === i) : ((answers[q.id] || []).includes(i));
                const showCorrect = (!examMode || submitted) && correctSet.includes(i);
                const showWrong = submitted && chosen && !correctSet.includes(i);
                return (
                  <label key={i} className={`mg-opt-line ${showCorrect ? 'correct' : ''} ${showWrong ? 'wrong' : ''}`}>
                    {examMode && !submitted ? (
                      q.type === 'mc-single' ? (
                        <input type="radio" name={`pv-${q.id}`} checked={chosen} onChange={() => setAns(q.id, i)} />
                      ) : (
                        <input type="checkbox" checked={chosen} onChange={e => {
                          const cur = new Set(answers[q.id] || []);
                          if (e.target.checked) cur.add(i); else cur.delete(i);
                          setAns(q.id, Array.from(cur));
                        }} />
                      )
                    ) : (
                      <span style={{ width: 18, display: 'inline-block' }}>{correctSet.includes(i) ? '✓' : ''}</span>
                    )}
                    <span>{String.fromCharCode(65+i)}. {opt || <em style={{ color:'#999' }}>(empty)</em>}</span>
                  </label>
                );
              })}

              {q.type === 'truefalse' && (
                <div style={{ display: 'flex', gap: 12 }}>
                  {['true','false'].map(v => (
                    <label key={v} className={`mg-opt-line ${(!examMode || submitted) && q.correct === v ? 'correct' : ''} ${submitted && answers[q.id] === v && answers[q.id] !== q.correct ? 'wrong' : ''}`}>
                      {examMode && !submitted ? (
                        <input type="radio" name={`pv-${q.id}`} checked={answers[q.id] === v} onChange={() => setAns(q.id, v)} />
                      ) : (
                        <span style={{ width: 18, display: 'inline-block' }}>{q.correct === v ? '✓' : ''}</span>
                      )}
                      {v === 'true' ? t('trueText', lang) : t('falseText', lang)}
                    </label>
                  ))}
                </div>
              )}

              {q.type === 'short' && (
                examMode && !submitted ? (
                  <input className="mg-input" value={answers[q.id] || ''} onChange={e => setAns(q.id, e.target.value)} />
                ) : (
                  <div><strong>{t('answer', lang)}: </strong>{(q.answers || []).join(' / ')}</div>
                )
              )}

              {q.type === 'numerical' && (
                examMode && !submitted ? (
                  <input type="number" className="mg-input" value={answers[q.id] || ''} onChange={e => setAns(q.id, e.target.value)} />
                ) : (
                  <div><strong>{t('answer', lang)}: </strong>{q.answer} (± {q.tolerance})</div>
                )
              )}

              {q.type === 'matching' && (
                <div>
                  {(q.pairs || []).map((p, i) => (
                    <div className="mg-row" key={i}>
                      <div style={{ minWidth: 200 }}>{p.q}</div>
                      {examMode && !submitted ? (
                        <select className="mg-select" value={(answers[q.id] && answers[q.id][i]) || ''}
                                onChange={e => {
                                  const cur = { ...(answers[q.id] || {}) };
                                  cur[i] = e.target.value;
                                  setAns(q.id, cur);
                                }}>
                          <option value="">--</option>
                          {[...new Set((q.pairs || []).map(pp => pp.a).filter(Boolean))].sort().map((a, k) => (
                            <option key={k} value={a}>{a}</option>
                          ))}
                        </select>
                      ) : (
                        <div style={{ color: '#059669' }}>→ {p.a}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {q.type === 'essay' && (
                examMode && !submitted ? (
                  <textarea className="mg-textarea" value={answers[q.id] || ''} onChange={e => setAns(q.id, e.target.value)} />
                ) : (
                  q.sampleAnswer && <div><strong>{t('sampleAnswer', lang)} </strong>{q.sampleAnswer}</div>
                )
              )}
            </div>
          ))}
        </div>
        <div className="mg-modal-foot">
          <button className="mg-btn ghost" style={{ width: 'auto', padding: '8px 14px' }} onClick={onClose}>{t('close', lang)}</button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
